import { trackingAllowed, safeAnalyticsParameters } from './analyticsPolicy';
import { isNative } from '../utils/platform';
import { trackNativeEvent } from './NativeAnalytics';
// Google Analytics 4 Service for tracking custom events
declare global {
  interface Window {
    gtag: any;
  }
}

export class GoogleAnalyticsService {
  private static instance: GoogleAnalyticsService;
  private isInitialized: boolean = false;

  private constructor() {}

  public static getInstance(): GoogleAnalyticsService {
    if (!GoogleAnalyticsService.instance) {
      GoogleAnalyticsService.instance = new GoogleAnalyticsService();
    }
    return GoogleAnalyticsService.instance;
  }

  // Initialize GA4 (called after page load)
  public init(): void {
    if (this.isInitialized || typeof window === 'undefined' || !trackingAllowed() || !window.gtag) {
      return;
    }

    try {
      // The Google tag owns initial/history page views; do not count another here.
      this.isInitialized = true;
      console.log('✅ Google Analytics initialized successfully');
    } catch (error) {
      console.error('❌ Google Analytics initialization failed:', error);
    }
  }

  // Track custom events
  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (isNative()) { void trackNativeEvent(eventName, parameters); return; }
    if (typeof window === 'undefined' || !trackingAllowed() || !window.gtag) {
      console.warn('Google Analytics not available');
      return;
    }

    try {
      // Financial details and user-entered text must stay out of analytics.
      window.gtag('event', eventName, { ...safeAnalyticsParameters(parameters), send_to: 'G-NNPDT7HP2K' });
    } catch (error) {
      console.error(`❌ Google Analytics: Failed to track ${eventName}:`, error);
    }
  }

  // Track goal-related events
  public trackGoalCreated(goalName: string, targetAmount: number, category: string): void {
    this.trackEvent('goal_created', {
      goal_name: goalName,
      target_amount: targetAmount,
      category: category,
      currency: 'USD'
    });
  }

  public trackGoalUpdated(goalName: string, savedAmount: number, progress: number): void {
    this.trackEvent('goal_updated', {
      goal_name: goalName,
      saved_amount: savedAmount,
      progress_percentage: progress,
      currency: 'USD'
    });
  }

  public trackGoalCompleted(goalName: string, totalAmount: number, timeToComplete: number): void {
    this.trackEvent('goal_completed', {
      goal_name: goalName,
      total_amount: totalAmount,
      time_to_complete_days: timeToComplete,
      currency: 'USD'
    });
  }

  public trackPaymentMade(amount: number, goalName?: string): void {
    this.trackEvent('payment_made', {
      amount: amount,
      goal_name: goalName || 'General',
      currency: 'USD'
    });
  }

  // Track user engagement
  public trackAppOpened(): void {
    this.trackEvent('app_opened');
  }

  public trackDashboardViewed(): void {
    this.trackEvent('dashboard_viewed');
  }

  public trackExpenseAdded(amount: number, category: string): void {
    this.trackEvent('expense_added', {
      amount: amount,
      category: category,
      currency: 'USD'
    });
  }

  public trackCategoryViewed(categoryName: string): void {
    this.trackEvent('category_viewed', {
      category_name: categoryName
    });
  }

  // Track conversion events
  public trackSignUp(method: string): void {
    this.trackEvent('sign_up', {
      method: method
    });
  }

  public trackSignupFormViewed(source?: string): void {
    this.trackEvent('signup_form_viewed', {
      funnel: 'activation',
      ...(source && { source })
    });
  }

  public trackSignupStarted(source?: string): void {
    this.trackEvent('signup_started', {
      funnel: 'activation',
      ...(source && { source })
    });
  }

  public trackSignupError(source: string, reason: string): void {
    this.trackEvent('signup_error', {
      funnel: 'activation',
      source,
      reason
    });
  }

  public trackLogin(method: string): void {
    this.trackEvent('login', {
      method: method
    });
  }

  public trackPremiumUpgrade(plan: string, source?: string): void {
    this.trackEvent('premium_upgrade', {
      plan: plan,
      ...(source && { source })
    });
  }

  /** Funnel: Monitoring. Use for % click CTA, % open upgrade modal, % convert, % complete connection. */
  public trackMonitoringCtaClick(): void {
    this.trackEvent('monitoring_cta_click', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringUpgradeModalOpen(): void {
    this.trackEvent('monitoring_upgrade_modal_open', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringUpgradeStarted(): void {
    this.trackEvent('monitoring_upgrade_started', { funnel: 'automatic_monitoring' });
  }

  public trackMonitoringConnectionComplete(insightCount?: number): void {
    this.trackEvent('monitoring_connection_complete', {
      funnel: 'automatic_monitoring',
      ...(typeof insightCount === 'number' && { insight_count: insightCount })
    });
  }

  public trackOnboardingViewed(): void {
    this.trackEvent('onboarding_viewed', { funnel: 'activation' });
  }

  public trackOnboardingStepViewed(step: number, stepName: string): void {
    this.trackEvent('onboarding_step_viewed', {
      funnel: 'activation',
      step,
      step_name: stepName
    });
  }

  public trackOnboardingChoice(choice: 'connect_bank' | 'demo_snapshot' | 'manual_setup' | 'skip'): void {
    this.trackEvent('onboarding_choice_selected', {
      funnel: 'activation',
      choice
    });
  }

  public trackOnboardingCompleted(source: string): void {
    this.trackEvent('onboarding_completed', {
      funnel: 'activation',
      source
    });
  }

  public trackEmptyDashboardViewed(source?: string): void {
    this.trackEvent('empty_dashboard_viewed', {
      funnel: 'activation',
      ...(source && { source })
    });
  }

  public trackFirstValueSeen(source: string): void {
    this.trackEvent('first_value_seen', {
      funnel: 'activation',
      source
    });
  }

  // Track app performance
  public trackAppError(errorType: string, errorMessage: string): void {
    this.trackEvent('app_error', {
      error_type: errorType,
      error_message: errorMessage
    });
  }

  public trackFeatureUsed(featureName: string): void {
    this.trackEvent('feature_used', {
      feature_name: featureName
    });
  }

  // Track page views for SPA routing
  public trackPageView(pagePath: string, pageTitle?: string): void {
    if (typeof window === 'undefined' || !trackingAllowed() || !window.gtag) {
      return;
    }

    try {
      window.gtag('event', 'page_view', {
        send_to: 'G-NNPDT7HP2K',
        page_path: pagePath,
        page_title: pageTitle || document.title
      });
    } catch (error) {
      console.error('❌ Google Analytics: Failed to track page view:', error);
    }
  }
}

// Export singleton instance
export const googleAnalytics = GoogleAnalyticsService.getInstance();
